import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import MasterPageBreadcrumb from "../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../layout/master/masterRouteMeta";
import MasterCard from "../../components/Master/MasterCard";
import { ROLES } from "../../constants/roles";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import AuthField from "../../components/Auth/AuthField";
import MasterSpinner from "../../components/Master/MasterSpinner";
import { normalizeContractDueDate } from "../../helpers/formatContractDueDate";
import { parseContractValueInput } from "../../helpers/formatContractValue";

const emptyPlanForm = () => ({
	mode: "catalog",
	planId: "",
	customName: "",
	monthlyTokens: 0,
	expiresAt: "",
	status: "ACTIVE",
	usedTokens: 0,
	extraTokens: 0,
	remainingTokens: 0,
	hasActive: false,
});

function buildPlanFormFromData(companyPlan) {
	if (!companyPlan) {
		return emptyPlanForm();
	}
	const isCustom = !companyPlan.planId;
	return {
		mode: isCustom ? "custom" : "catalog",
		planId: companyPlan.planId != null ? String(companyPlan.planId) : "",
		customName: companyPlan.customName || "",
		monthlyTokens: companyPlan.monthlyTokens ?? 0,
		expiresAt: normalizeContractDueDate(companyPlan.expiresAt) || "",
		status: companyPlan.status || "ACTIVE",
		usedTokens: companyPlan.usedTokens ?? 0,
		extraTokens: companyPlan.extraTokens ?? 0,
		remainingTokens: companyPlan.remainingTokens ?? 0,
		hasActive: true,
	};
}

export default function EditCompany() {
	const history = useHistory();
	const { id } = useParams();
	const { user, loading: authLoading } = useContext(AuthContext);
	const [company, setCompany] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [savingPlan, setSavingPlan] = useState(false);
	const [catalogPlans, setCatalogPlans] = useState([]);
	const [form, setForm] = useState({
		name: "",
		userLimit: 2,
		connectionLimit: 5,
		isActive: true,
		contractDetails: "",
		contractDueDate: "",
		contractValue: "",
	});
	const [planForm, setPlanForm] = useState(emptyPlanForm());

	useEffect(() => {
		if (authLoading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace("/");
			return;
		}
		if (!id) {
			history.replace("/admin-master/companies");
			return;
		}

		Promise.all([
			api.get(`/master/companies/${id}`),
			api.get("/master/plans").catch(() => {
				toast.error(i18n.t("master.editCompany.planLoadError"));
				return { data: [] };
			}),
		])
			.then(([companyRes, plansRes]) => {
				const data = companyRes.data;
				setCompany(data);
				setForm({
					name: data.name || "",
					userLimit: data.userLimit ?? 2,
					connectionLimit: data.connectionLimit ?? 5,
					isActive: data.isActive !== false,
					contractDetails: data.contractDetails || "",
					contractDueDate: normalizeContractDueDate(data.contractDueDate) || "",
					contractValue:
						data.contractValue != null && data.contractValue !== ""
							? String(data.contractValue)
							: "",
				});
				setPlanForm(buildPlanFormFromData(data.companyPlan));
				const plans = Array.isArray(plansRes.data) ? plansRes.data : [];
				setCatalogPlans(plans.filter((p) => p.active !== false));
			})
			.catch((err) => {
				toastError(err);
				history.replace("/admin-master/companies");
			})
			.finally(() => setLoading(false));
	}, [id, user, authLoading, history]);

	const onSelectCatalogPlan = (e) => {
		const planId = e.target.value;
		const selected = catalogPlans.find((p) => String(p.id) === String(planId));
		setPlanForm((f) => ({
			...f,
			planId,
			monthlyTokens: selected ? selected.monthlyTokens ?? 0 : f.monthlyTokens,
		}));
	};

	const buildPlanPayload = () => ({
		mode: planForm.mode,
		planId: planForm.mode === "catalog" ? Number(planForm.planId) : null,
		customName: planForm.mode === "custom" ? planForm.customName.trim() : null,
		monthlyTokens: Math.max(0, Math.floor(Number(planForm.monthlyTokens) || 0)),
		expiresAt: planForm.expiresAt || null,
		status: planForm.status,
	});

	const validatePlanForm = () => {
		if (planForm.mode === "catalog" && !planForm.planId) {
			toast.error(i18n.t("master.editCompany.planCatalogPlaceholder"));
			return false;
		}
		if (planForm.mode === "custom" && planForm.customName.trim().length < 2) {
			toast.error(i18n.t("master.editCompany.planCustomNameRequired"));
			return false;
		}
		return true;
	};

	const shouldPersistPlan = () => {
		if (planForm.hasActive) return true;
		if (planForm.mode === "custom" && planForm.customName.trim().length >= 2) {
			return true;
		}
		if (planForm.mode === "catalog" && planForm.planId) {
			return true;
		}
		return false;
	};

	const handleSave = async () => {
		if (!company) return;
		if (shouldPersistPlan() && !validatePlanForm()) return;

		setSaving(true);
		try {
			await api.put(`/master/companies/${company.id}`, {
				name: form.name.trim() || undefined,
				userLimit: form.userLimit,
				connectionLimit: form.connectionLimit,
				isActive: form.isActive,
				contractDetails: form.contractDetails.trim() || null,
				contractDueDate: form.contractDueDate || null,
				contractValue: parseContractValueInput(form.contractValue),
			});

			if (shouldPersistPlan()) {
				await api.put(
					`/master/companies/${company.id}/plan`,
					buildPlanPayload()
				);
			}

			toast.success(i18n.t("master.editCompany.companyAndPlanSaved"));
			history.push("/admin-master/companies");
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleSavePlan = () => {
		if (!company) return;
		if (!validatePlanForm()) return;
		setSavingPlan(true);
		api
			.put(`/master/companies/${company.id}/plan`, buildPlanPayload())
			.then(({ data }) => {
				setPlanForm(buildPlanFormFromData(data));
				toast.success(i18n.t("master.editCompany.planSaved"));
			})
			.catch((err) => toastError(err))
			.finally(() => setSavingPlan(false));
	};

	const formatCnpj = (value) => {
		if (value == null || value === "") return "—";
		const digits = String(value).replace(/\D/g, "");
		if (digits.length !== 14) return value;
		return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
	};

	if (authLoading || !user || user.profile !== ROLES.master) {
		return null;
	}

	const routeMeta = getMasterRouteMeta(`/admin-master/companies/${id}/edit`);

	if (loading) {
		return (
			<div className="mx-auto w-full max-w-3xl">
				<MasterPageBreadcrumb
					title={i18n.t(routeMeta.titleKey)}
					breadcrumbs={routeMeta.breadcrumbs}
				/>
				<div className="master-loading">
					<MasterSpinner />
					<span>{i18n.t("master.pages.loadingCompany")}</span>
				</div>
			</div>
		);
	}

	if (!company) {
		return null;
	}

	return (
		<div className="mx-auto w-full max-w-3xl box-border">
			<MasterPageBreadcrumb
				title={i18n.t(routeMeta.titleKey)}
				breadcrumbs={routeMeta.breadcrumbs}
			/>
			<p className="-mt-2 mb-6 text-sm text-default-500">
				{company.name || i18n.t("master.pages.companiesListTitle")}
			</p>
			<MasterCard>
				<div className="text-center mb-8">
					<h2 className="text-xl font-bold text-default-900 dark:text-default-50 m-0">
						Dados da empresa
					</h2>
					<p className="text-sm text-default-500 mt-2 mb-0">
						Altere o nome, limites de uso e informações do contrato.
					</p>
				</div>

				<hr className="border-default-200 dark:border-default-600 mb-8" />

				<AuthField
					id="edit-company-name"
					label="Nome"
					hint="Nome exibido para identificar a empresa no sistema."
				>
					<input
						id="edit-company-name"
						type="text"
						className="form-input w-full"
						value={form.name}
						onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
						placeholder="Ex: Minha Empresa Ltda"
					/>
				</AuthField>

				<AuthField id="edit-company-document" label="CNPJ">
					<input
						id="edit-company-document"
						type="text"
						className="form-input w-full font-mono opacity-80 cursor-not-allowed"
						value={formatCnpj(company.document)}
						readOnly
						aria-readonly="true"
					/>
				</AuthField>

				<AuthField
					id="edit-company-limit"
					label="Limite de usuários"
					hint="Número máximo de usuários (perfil usuário) além do administrador."
				>
					<input
						id="edit-company-limit"
						type="number"
						min={1}
						max={999}
						className="form-input w-full"
						value={form.userLimit}
						onChange={(e) =>
							setForm((f) => ({
								...f,
								userLimit: parseInt(e.target.value, 10) || 1,
							}))
						}
					/>
				</AuthField>

				<AuthField
					id="edit-company-connection-limit"
					label="Limite de conexões"
					hint="Número máximo de conexões (WhatsApp, Telegram, etc.) que a empresa pode cadastrar."
				>
					<input
						id="edit-company-connection-limit"
						type="number"
						min={1}
						max={999}
						className="form-input w-full"
						value={form.connectionLimit}
						onChange={(e) => {
							const v = parseInt(e.target.value, 10);
							setForm((f) => ({
								...f,
								connectionLimit: Number.isNaN(v)
									? 5
									: Math.max(1, Math.min(999, v)),
							}));
						}}
					/>
				</AuthField>

				<hr className="border-default-200 dark:border-default-600 my-8" />

				<div className="text-center mb-6">
					<h3 className="text-lg font-bold text-default-900 dark:text-default-50 m-0">
						{i18n.t("master.pages.contractSectionTitle")}
					</h3>
					<p className="text-sm text-default-500 mt-2 mb-0">
						{i18n.t("master.pages.contractSectionHint")}
					</p>
				</div>

				<AuthField
					id="edit-company-contract-details"
					label={i18n.t("master.pages.contractDetailsLabel")}
					hint={i18n.t("master.pages.contractDetailsHint")}
				>
					<textarea
						id="edit-company-contract-details"
						className="form-input w-full min-h-[120px] resize-y"
						value={form.contractDetails}
						onChange={(e) =>
							setForm((f) => ({ ...f, contractDetails: e.target.value }))
						}
						placeholder={i18n.t("master.pages.contractDetailsPlaceholder")}
					/>
				</AuthField>

				<AuthField
					id="edit-company-contract-value"
					label={i18n.t("master.pages.contractValueLabel")}
					hint={i18n.t("master.pages.contractValueHint")}
				>
					<input
						id="edit-company-contract-value"
						type="number"
						min={0}
						step="0.01"
						className="form-input w-full max-w-xs"
						value={form.contractValue}
						onChange={(e) =>
							setForm((f) => ({ ...f, contractValue: e.target.value }))
						}
						placeholder={i18n.t("master.pages.contractValuePlaceholder")}
					/>
				</AuthField>

				<AuthField
					id="edit-company-contract-due-date"
					label={i18n.t("master.pages.contractDueDateLabel")}
					hint={i18n.t("master.pages.contractDueDateHint")}
				>
					<input
						id="edit-company-contract-due-date"
						type="date"
						className="form-input w-full max-w-xs"
						value={form.contractDueDate}
						onChange={(e) =>
							setForm((f) => ({ ...f, contractDueDate: e.target.value }))
						}
					/>
				</AuthField>

				<AuthField
					hint="Empresa inativa bloqueia acesso de todos os usuários vinculados até a reativação."
				>
					<label className="flex items-center gap-3 cursor-pointer mt-2">
						<input
							type="checkbox"
							className="w-5 h-5 rounded border-default-300 text-primary focus:ring-primary/30"
							checked={form.isActive}
							onChange={(e) =>
								setForm((f) => ({ ...f, isActive: e.target.checked }))
							}
						/>
						<span className="text-sm font-medium text-default-800 dark:text-default-200">
							Empresa ativa
						</span>
					</label>
				</AuthField>

				<hr className="border-default-200 dark:border-default-600 my-8" />

				<div className="text-center mb-6">
					<h3 className="text-lg font-bold text-default-900 dark:text-default-50 m-0">
						{i18n.t("master.editCompany.planSectionTitle")}
					</h3>
					<p className="text-sm text-default-500 mt-2 mb-0">
						{i18n.t("master.editCompany.planSectionHint")}
					</p>
				</div>

				{!planForm.hasActive && (
					<p className="text-sm text-default-500 mb-4">
						{i18n.t("master.editCompany.planNoActive")}
					</p>
				)}

				<AuthField
					id="edit-company-plan-mode"
					label={i18n.t("master.editCompany.planModeLabel")}
				>
					<select
						id="edit-company-plan-mode"
						className="form-input w-full max-w-xs"
						value={planForm.mode}
						onChange={(e) =>
							setPlanForm((f) => ({
								...f,
								mode: e.target.value,
							}))
						}
					>
						<option value="catalog">
							{i18n.t("master.editCompany.planModeCatalog")}
						</option>
						<option value="custom">
							{i18n.t("master.editCompany.planModeCustom")}
						</option>
					</select>
				</AuthField>

				{planForm.mode === "catalog" ? (
					<AuthField
						id="edit-company-plan-catalog"
						label={i18n.t("master.editCompany.planCatalogLabel")}
					>
						<select
							id="edit-company-plan-catalog"
							className="form-input w-full"
							value={planForm.planId}
							onChange={onSelectCatalogPlan}
						>
							<option value="">
								{i18n.t("master.editCompany.planCatalogPlaceholder")}
							</option>
							{catalogPlans.map((p) => (
								<option key={p.id} value={p.id}>
									{p.name} ({p.monthlyTokens ?? 0} tokens)
								</option>
							))}
						</select>
					</AuthField>
				) : (
					<AuthField
						id="edit-company-plan-custom-name"
						label={i18n.t("master.editCompany.planCustomNameLabel")}
					>
						<input
							id="edit-company-plan-custom-name"
							type="text"
							className="form-input w-full"
							value={planForm.customName}
							onChange={(e) =>
								setPlanForm((f) => ({ ...f, customName: e.target.value }))
							}
							placeholder={i18n.t("master.editCompany.planCustomNamePlaceholder")}
						/>
					</AuthField>
				)}

				<AuthField
					id="edit-company-plan-monthly-tokens"
					label={i18n.t("master.editCompany.planMonthlyTokensLabel")}
					hint={i18n.t("master.editCompany.planMonthlyTokensHint")}
				>
					<input
						id="edit-company-plan-monthly-tokens"
						type="number"
						min={0}
						className="form-input w-full max-w-xs"
						value={planForm.monthlyTokens}
						onChange={(e) =>
							setPlanForm((f) => ({
								...f,
								monthlyTokens: Math.max(0, parseInt(e.target.value, 10) || 0),
							}))
						}
					/>
				</AuthField>

				<AuthField
					id="edit-company-plan-expires"
					label={i18n.t("master.editCompany.planExpiresAtLabel")}
					hint={i18n.t("master.editCompany.planExpiresAtHint")}
				>
					<input
						id="edit-company-plan-expires"
						type="date"
						className="form-input w-full max-w-xs"
						value={planForm.expiresAt}
						onChange={(e) =>
							setPlanForm((f) => ({ ...f, expiresAt: e.target.value }))
						}
					/>
				</AuthField>

				<AuthField
					id="edit-company-plan-status"
					label={i18n.t("master.editCompany.planStatusLabel")}
				>
					<select
						id="edit-company-plan-status"
						className="form-input w-full max-w-xs"
						value={planForm.status}
						onChange={(e) =>
							setPlanForm((f) => ({ ...f, status: e.target.value }))
						}
					>
						<option value="ACTIVE">
							{i18n.t("master.editCompany.planStatusActive")}
						</option>
						<option value="CANCELED">
							{i18n.t("master.editCompany.planStatusCanceled")}
						</option>
						<option value="EXPIRED">
							{i18n.t("master.editCompany.planStatusExpired")}
						</option>
					</select>
				</AuthField>

				{planForm.hasActive && (
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
						<div>
							<p className="text-xs text-default-500 m-0 mb-1">
								{i18n.t("master.editCompany.planUsedTokens")}
							</p>
							<p className="text-sm font-semibold text-default-900 dark:text-default-50 m-0">
								{planForm.usedTokens}
							</p>
						</div>
						<div>
							<p className="text-xs text-default-500 m-0 mb-1">
								{i18n.t("master.editCompany.planExtraTokens")}
							</p>
							<p className="text-sm font-semibold text-default-900 dark:text-default-50 m-0">
								{planForm.extraTokens}
							</p>
						</div>
						<div>
							<p className="text-xs text-default-500 m-0 mb-1">
								{i18n.t("master.editCompany.planRemainingTokens")}
							</p>
							<p className="text-sm font-semibold text-default-900 dark:text-default-50 m-0">
								{planForm.remainingTokens}
							</p>
						</div>
					</div>
				)}

				<div className="flex flex-wrap gap-3 justify-end mb-2">
					<button
						type="button"
						className="btn-primary"
						onClick={handleSavePlan}
						disabled={savingPlan || saving}
					>
						{savingPlan
							? i18n.t("master.editCompany.planSaving")
							: i18n.t("master.editCompany.planSave")}
					</button>
				</div>

				<div className="flex flex-wrap gap-3 justify-end mt-8">
					<button
						type="button"
						className="btn-secondary"
						onClick={() => history.push("/admin-master/companies")}
						disabled={saving || savingPlan}
					>
						{i18n.t("userModal.buttons.cancel")}
					</button>
					<button
						type="button"
						className="btn-primary"
						onClick={handleSave}
						disabled={saving || savingPlan}
					>
						{saving ? i18n.t("master.pages.saving") : i18n.t("userModal.buttons.okEdit")}
					</button>
				</div>
			</MasterCard>
		</div>
	);
}
